/**
 * Media (docs/wiring-documentation-proposal.md, "Media"): ATEM media pool
 * content, stored once in S3 by its sha256 and referenced from config data.
 *
 * - `media_assets` (cloud, studio-scoped) describes each stored object: one
 *   row per studio and hash. Configs that share a still share the object, so
 *   copying a config copies no bytes.
 * - `config_data.media_pool[deviceID].stills[index]` says which asset sits in
 *   which slot of an ATEM's media pool. `index` is the 0-based still index,
 *   the same `N` as `/mediaPool/stills/N` in the device state. The config owns
 *   the whole pool: a load clears slots that the config leaves empty.
 *
 * Stills only for now. Clips can be downloaded frame by frame and audio not
 * at all by the ATEM library; both are reported as warnings by capture and
 * restore (follow-up).
 */
export const MEDIA_KINDS = ["still", "clip", "audio"];
/** Every stored object lives under this prefix, keyed by its sha256. */
export const MEDIA_S3_PREFIX = "studio-media/";
/** Still pool size when the switcher's real size is unknown (cloud editor). */
export const DEFAULT_STILL_POOL_SIZE = 20;
const SHA256_RE = /^[0-9a-f]{64}$/;
export function isSha256(value) {
    return typeof value === "string" && SHA256_RE.test(value);
}
export function mediaS3Key(sha256) {
    if (!isSha256(sha256))
        throw new Error(`Not a sha256: ${sha256}`);
    return `${MEDIA_S3_PREFIX}${sha256}`;
}
/** Every asset hash a config's media pool and playout reference. */
export function configMediaHashes(data) {
    const hashes = new Set();
    for (const pool of Object.values(data.media_pool ?? {})) {
        for (const slot of Object.values(pool?.stills ?? {})) {
            if (slot?.asset && isSha256(slot.asset))
                hashes.add(slot.asset);
        }
    }
    for (const asset of data.playout?.assets ?? []) {
        if (asset.media && isSha256(asset.media))
            hashes.add(asset.media);
    }
    return [...hashes];
}
/**
 * Lowest free still slot of a pool (0-based), or null when the pool is full.
 * `occupied` adds slots that are used on the switcher but not (yet) in the
 * config, so live placement never overwrites a still nobody captured.
 */
export function allocateStillSlot(pool, poolSize, occupied = []) {
    const taken = new Set([
        ...Object.keys(pool?.stills ?? {}).map(Number),
        ...occupied,
    ]);
    for (let index = 0; index < poolSize; index++) {
        if (!taken.has(index))
            return index;
    }
    return null;
}
/** The slot holding a playout asset in a pool, or null. */
export function findPlayoutSlot(pool, playoutAssetID) {
    for (const [index, slot] of Object.entries(pool?.stills ?? {})) {
        if (slot.use === "playout" && slot.playout_asset_id === playoutAssetID) {
            return Number(index);
        }
    }
    return null;
}
/** Still pool size seen in device state (`/mediaPool/stills/N/...`). */
export function stillPoolSizeFromState(values) {
    let max = -1;
    for (const path of Object.keys(values ?? {})) {
        const match = /^\/mediaPool\/stills\/(\d+)\//.exec(path);
        if (match)
            max = Math.max(max, Number(match[1]));
    }
    return max >= 0 ? max + 1 : null;
}
